import { Base, ButtonLink, H1, Lead, Spinner } from '@sbab-ui/components'
import { useEffect } from 'react'
import { name as appName } from '../../../package.json'
import { fetchDataForResultSection } from '../../api/httpHandlers'
import { AppAction, AppStatus, InterestRateAppState } from '../../appState'
import {
  getMonthlyInterestCost,
  getMonthlyInterestCostWithTaxDeduction,
} from '../../lib/calculationUtils'

interface Props {
  status: AppStatus
  interestRateAppDispatch: React.Dispatch<AppAction>
  interestRateAppState: InterestRateAppState
  blur: boolean
}

export default function Result({
  status,
  interestRateAppDispatch,
  interestRateAppState,
  blur,
}: Readonly<Props>) {
  const {
    userValues,
    formIsValid,
    threeMonthInterestRate,
    hasFetchedSucessfully,
    priceMatrixDifference,
    kilowattLoanDifference,
  } = interestRateAppState
  const { estimatedValue, mortgage, wantedLoanAmount } = userValues
  const loanAmount = mortgage + wantedLoanAmount

  useEffect(() => {
    // Fetch data when form is valid or when user changes input
    async function fetchData() {
      interestRateAppDispatch({ type: 'statusChange', status: 'fetching' })
      try {
        const response = await fetchDataForResultSection(
          estimatedValue,
          loanAmount,
          threeMonthInterestRate,
        )
        if (!response.successful) return interestRateAppDispatch({ type: 'error' })
        const { priceMatrixDifference, kilowattLoanDifference, effectiveInterest } = response
        interestRateAppDispatch({
          type: 'resultSectionDataFetched',
          priceMatrixDifference,
          kilowattLoanDifference,
          effectiveInterest,
        })
      } catch (error) {
        interestRateAppDispatch({ type: 'error' })
      }
    }
    if (formIsValid) {
      fetchData().catch(() => {
        interestRateAppDispatch({ type: 'error' })
      })
    }
  }, [estimatedValue, formIsValid, interestRateAppDispatch, threeMonthInterestRate, loanAmount])

  useEffect(() => {
    if (status === 'success' && !hasFetchedSucessfully) {
      interestRateAppDispatch({ type: 'setHasFetchedSucessfully' })
    }
  }, [status, hasFetchedSucessfully, interestRateAppDispatch])

  const userInterest = threeMonthInterestRate + priceMatrixDifference + kilowattLoanDifference
  // Totals
  const monthlyInterest = getMonthlyInterestCost(userInterest, wantedLoanAmount)
  const monthlyInterestWithTaxDeduction = getMonthlyInterestCostWithTaxDeduction(
    userInterest,
    wantedLoanAmount,
  )
  return (
    <div
      className={`relative flex flex-1 flex-col gap-8 ${blur ? 'text-neutral-95 opacity-50' : 'text-black'}`}
    >
      {status === 'fetching' && (
        <div className="absolute z-20 flex h-96 w-full max-w-[784px] items-center justify-center">
          <Spinner size="large" data-cy="spinner" />
        </div>
      )}
      {hasFetchedSucessfully ? (
        <div className="flex flex-col gap-[6px]">
          <Lead className="!mt-0">Räntekostnad efter skatteavdrag</Lead>
          <H1 className="!mt-0" as="p">
            {monthlyInterestWithTaxDeduction.toLocaleString('sv-SE')} kr/mån
          </H1>
          <Base className="!mt-0">
            Före skatteavdrag {monthlyInterest.toLocaleString('sv-SE')} kr/mån
          </Base>
        </div>
      ) : (
        <Lead className="!mt-0 text-neutral-40">Fyll i uppgifterna för att se din ränta</Lead>
      )}
      <div className="flex flex-col gap-6">
        <ButtonLink
          variant="primary"
          className="!max-w-max !px-10"
          href={`https://secure.sbab.se/logga-in?ref=${appName}`}
        >
          Logga in och höj ditt bolån
        </ButtonLink>
        <ButtonLink
          variant="secondary"
          className="!max-w-max !px-10"
          href={`https://www.sbab.se/1/privat/lana/bolan/flytta_bolan_till_oss.html?ref=${appName}`}
        >
          Flytta ditt bolån till oss
        </ButtonLink>
      </div>
    </div>
  )
}
